import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Bookmark, BookmarkCheck, Save, ExternalLink, Calendar, User, Hash } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TweetCard = ({ tweet, onAnnotationUpdate }) => {
  const [tag, setTag] = useState(tweet.annotation?.tag || '');
  const [note, setNote] = useState(tweet.annotation?.note || '');
  const [translation, setTranslation] = useState(tweet.annotation?.translation || '');
  const [showTranslation, setShowTranslation] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(tweet.annotation?.is_bookmarked || false);
  const [saving, setSaving] = useState(false);
  const [tweetLoading, setTweetLoading] = useState(true);
  const [tweetNotFound, setTweetNotFound] = useState(false);
  const tweetContainerRef = useRef(null);
  const { toast} = useToast();

  // Reset form fields when tweet changes
  useEffect(() => {
    // Join tags array back to comma-separated string for display
    const tagsString = tweet.annotation?.tags ? tweet.annotation.tags.join(', ') : '';
    setTag(tagsString);
    setNote(tweet.annotation?.note || '');
    setTranslation(tweet.annotation?.translation || '');
    setIsBookmarked(tweet.annotation?.is_bookmarked || false);
    // Show translation if it exists for this tweet
    setShowTranslation(!!tweet.annotation?.translation);
  }, [tweet.id]); // Reset whenever tweet ID changes

  useEffect(() => {
    // Load Twitter embed when component mounts or tweet changes
    const loadTweet = () => {
      if (tweetContainerRef.current && window.twttr && window.twttr.widgets) {
        // Clear previous content
        tweetContainerRef.current.innerHTML = '';
        setTweetLoading(true);
        setTweetNotFound(false);
        
        // Extract tweet ID from URL
        const tweetId = tweet.tweetUrl.split('/status/')[1]?.split('?')[0];
        
        if (tweetId) {
          // Set a timeout to handle tweets that fail to load
          const loadTimeout = setTimeout(() => {
            setTweetLoading(false);
            setTweetNotFound(true);
          }, 5000); // 5 second timeout
          
          window.twttr.widgets.createTweet(
            tweetId,
            tweetContainerRef.current,
            {
              theme: 'light',
              align: 'center',
              conversation: 'none',
              cards: 'visible',
              width: 550
            }
          ).then((embedElement) => {
            clearTimeout(loadTimeout);
            setTweetLoading(false);
            // If embedElement is null, the tweet was not found
            if (!embedElement) {
              setTweetNotFound(true);
            }
          }).catch((error) => {
            clearTimeout(loadTimeout);
            console.error('Error loading tweet:', error);
            setTweetLoading(false);
            setTweetNotFound(true);
          });
        } else {
          setTweetLoading(false);
          setTweetNotFound(true);
        }
      }
    };

    // Wait for Twitter widget script to load
    if (window.twttr && window.twttr.widgets) {
      loadTweet();
    } else {
      // Poll for Twitter widget availability
      const checkTwitter = setInterval(() => {
        if (window.twttr && window.twttr.widgets) {
          clearInterval(checkTwitter);
          loadTweet();
        }
      }, 100);

      // Cleanup
      return () => clearInterval(checkTwitter);
    }
  }, [tweet.tweetUrl]);

  const handleTranslate = async () => {
    setTranslating(true);
    try {
      const response = await axios.post(`${API}/translate`, {
        text: tweet.content
      }, {
        withCredentials: true
      });

      setTranslation(response.data.translation);
      setShowTranslation(true);
      
      toast({
        title: 'Translation complete',
        description: `Detected: ${response.data.detected_language}`,
      });
    } catch (error) {
      console.error('Error translating:', error);
      toast({
        title: 'Translation failed',
        description: 'Please try again.',
        variant: 'destructive'
      });
    } finally {
      setTranslating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Split tags by comma and trim each one
      const tagsArray = tag.split(',').map(t => t.trim()).filter(t => t.length > 0);
      
      const response = await axios.post(`${API}/annotations`, {
        tweet_id: tweet.id,
        tags: tagsArray,
        note: note.trim(),
        translation: translation.trim(),
        is_bookmarked: isBookmarked
      }, {
        withCredentials: true
      });

      onAnnotationUpdate(tweet.id, response.data);
      
      toast({
        title: 'Saved successfully',
        description: `Saved with ${tagsArray.length} tag(s)`,
      });
    } catch (error) {
      console.error('Error saving annotation:', error);
      toast({
        title: 'Error',
        description: 'Failed to save annotation. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleBookmark = async () => {
    const newBookmarkState = !isBookmarked;
    setIsBookmarked(newBookmarkState);
    
    try {
      const tagsArray = tag.split(',').map(t => t.trim()).filter(t => t.length > 0);
      
      const response = await axios.post(`${API}/annotations`, {
        tweet_id: tweet.id,
        tags: tagsArray,
        note: note.trim() || null,
        translation: translation.trim() || null,
        is_bookmarked: newBookmarkState
      }, {
        withCredentials: true
      });

      onAnnotationUpdate(tweet.id, response.data);
      
      toast({
        title: newBookmarkState ? 'Bookmarked' : 'Bookmark removed',
        description: newBookmarkState ? 'Tweet added to bookmarks' : 'Tweet removed from bookmarks',
      });
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      setIsBookmarked(!newBookmarkState);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="tweet-card">
      {/* Left Side - Tweet Details & Annotation */}
      <Card className="shadow-lg">
        <CardHeader className="space-y-4">
          {/* Tweet Metadata */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(tweet.tweetDate)}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleBookmark}
              data-testid="bookmark-button"
            >
              {isBookmarked ? (
                <BookmarkCheck className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              ) : (
                <Bookmark className="w-5 h-5 text-gray-400" />
              )}
            </Button>
          </div>

          {/* Username */}
          <div className="flex items-center space-x-2">
            <User className="w-4 h-4 text-gray-600" />
            <span className="font-medium text-gray-900">@{tweet.username || 'Unknown'}</span>
            <a
              href={tweet.twitterProfile}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          {/* Query Badge */}
          {tweet.query && (
            <Badge variant="secondary" className="w-fit">
              <Hash className="w-3 h-3 mr-1" />
              {tweet.query}
            </Badge>
          )}
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Tweet Content with RTL Support */}
          <div
            className="bg-gray-50 p-6 rounded-lg text-lg leading-relaxed"
            style={{ direction: 'rtl', fontFamily: 'Segoe UI, Tahoma, sans-serif' }}
            data-testid="tweet-content"
          >
            {tweet.content}
          </div>

          {/* Hashtags */}
          {tweet.hashtags && tweet.hashtags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {tweet.hashtags.map((hashtag, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {hashtag}
                </Badge>
              ))}
            </div>
          )}

          {/* Tweet Link */}
          <a
            href={tweet.tweetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 text-sm flex items-center space-x-2"
            data-testid="tweet-url-link"
          >
            <ExternalLink className="w-4 h-4" />
            <span>View original tweet</span>
          </a>

          {/* Translation Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!translation) {
                    handleTranslate();
                  } else {
                    setShowTranslation(!showTranslation);
                  }
                }}
                disabled={translating}
                data-testid="translate-button"
              >
                {translating ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600 mr-2"></div>
                    Translating...
                  </>
                ) : (
                  <>
                    {showTranslation ? '🇬🇧 Hide Translation' : '🇬🇧 Translate to English'}
                  </>
                )}
              </Button>
            </div>

            {showTranslation && translation && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  English Translation (editable)
                </label>
                <Textarea
                  value={translation}
                  onChange={(e) => setTranslation(e.target.value)}
                  placeholder="Translation will appear here..."
                  rows={4}
                  className="bg-blue-50"
                  data-testid="translation-input"
                />
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t pt-6">
            <h3 className="font-semibold text-gray-900 mb-4">Annotation</h3>
            
            {/* Tag Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Tags (comma-separated)
              </label>
              <Input
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                placeholder="e.g., grief, support, silencing"
                data-testid="tag-input"
              />
              <p className="text-xs text-gray-500">Separate multiple tags with commas</p>
            </div>

            {/* Note Input */}
            <div className="space-y-2 mt-4">
              <label className="text-sm font-medium text-gray-700">
                Notes
              </label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add your analysis notes..."
                rows={6}
                data-testid="note-input"
              />
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full mt-4"
              data-testid="save-annotation-button"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Annotation
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Right Side - Tweet Preview */}
      <Card className="shadow-lg">
        <CardHeader>
          <h3 className="font-semibold text-gray-900">Tweet Preview</h3>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center" style={{ minHeight: '500px' }}>
            {tweet.tweetUrl ? (
              <div className="w-full">
                {tweetLoading && !tweetNotFound && (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-gray-500 text-sm">Loading tweet preview...</p>
                  </div>
                )}
                {tweetNotFound && (
                  <div className="flex flex-col items-center justify-center py-8 px-6 max-w-md mx-auto">
                    <div className="text-5xl mb-4">📭</div>
                    <h4 className="text-lg font-semibold text-gray-700 mb-2">Tweet Unavailable</h4>
                    <p className="text-sm text-gray-600 text-center mb-4">
                      This tweet may have been deleted, the account may be suspended, or the tweet may be private.
                    </p>
                    <div className="w-full bg-white border border-gray-200 rounded-lg p-4 mt-2">
                      <p className="text-xs text-gray-500 mb-2">Archived Content:</p>
                      <div 
                        className="text-sm text-gray-800 leading-relaxed"
                        style={{ direction: 'rtl', fontFamily: 'Segoe UI, Tahoma, sans-serif' }}
                      >
                        {tweet.content}
                      </div>
                      <a
                        href={tweet.tweetUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-xs mt-3 flex items-center"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Try opening original link
                      </a>
                    </div>
                  </div>
                )}
                <div ref={tweetContainerRef} className="flex justify-center"></div>
              </div>
            ) : (
              <p className="text-gray-500 text-center">No preview available</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TweetCard;